
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShoppingBag, Settings } from 'lucide-react';
import { Integration } from '@/types/integrations';
import { useNuvemshopConnection } from './nuvemshop/hooks/useNuvemshopConnection';
import { useNuvemshopSync } from './nuvemshop/hooks/useNuvemshopSync';
import { useNuvemshopConfig } from './nuvemshop/hooks/useNuvemshopConfig';
import { NuvemshopStatusBadge } from './nuvemshop/components/NuvemshopStatusBadge';
import { NuvemshopConnectionState } from './nuvemshop/components/NuvemshopConnectionState';
import { NuvemshopActiveState } from './nuvemshop/components/NuvemshopActiveState';
import { NuvemshopErrorState } from './nuvemshop/components/NuvemshopErrorState';

interface NuvemshopIntegrationProps {
  integration?: Integration;
}

export const NuvemshopIntegration = ({ integration }: NuvemshopIntegrationProps) => {
  console.log('🔄 NuvemshopIntegration: Renderizando', {
    hasIntegration: !!integration,
    integrationId: integration?.id,
    status: integration?.status,
    connectionStatus: integration?.connection_status,
    integrationType: integration?.integration_type
  });

  const connectionHandlers = useNuvemshopConnection(integration);
  const syncHandlers = useNuvemshopSync(integration);
  const configHandlers = useNuvemshopConfig(integration);

  const renderContent = () => {
    // Se não há integração, mostrar estado de conexão
    if (!integration) {
      console.log('🎯 NuvemshopIntegration: Sem integração - mostrando estado de conexão');
      return <NuvemshopConnectionState {...connectionHandlers} />;
    }

    console.log('🎯 NuvemshopIntegration: Integração encontrada', {
      status: integration.status,
      connectionStatus: integration.connection_status
    });

    // Se está ativa e conectada, mostrar estado ativo
    if (integration.status === 'active' && integration.connection_status === 'connected') {
      console.log('🎯 NuvemshopIntegration: Renderizando estado ativo');
      return (
        <NuvemshopActiveState 
          integration={integration}
          {...connectionHandlers}
          {...syncHandlers}
          {...configHandlers}
        />
      );
    }

    // Se está ativa mas não conectada, ou em erro
    if (integration.status === 'error' || integration.connection_status === 'error') {
      console.log('🎯 NuvemshopIntegration: Renderizando estado de erro');
      return (
        <NuvemshopErrorState 
          integration={integration}
          {...connectionHandlers}
        />
      );
    }

    // Estado de configuração ou outros
    console.log('🎯 NuvemshopIntegration: Estado indefinido, mostrando erro', {
      status: integration.status,
      connectionStatus: integration.connection_status
    });
    
    return (
      <NuvemshopErrorState 
        integration={integration}
        {...connectionHandlers}
      />
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShoppingBag className="h-6 w-6 text-blue-600" />
            <div>
              <CardTitle>Nuvemshop</CardTitle>
              <CardDescription>
                Integração com e-commerce para sincronização de dados
              </CardDescription>
            </div>
          </div>
          {integration && (
            <div className="flex items-center gap-2">
              <NuvemshopStatusBadge status={integration.status} />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => configHandlers.setIsConfiguring(!configHandlers.isConfiguring)}
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {renderContent()}
      </CardContent>
    </Card>
  );
};
